export interface ICoordinates {
  lat: number;
  lon: number;
};

//Haversine formula - to calculate the shortest distance between two points
export function getDistanceFromLatLonInKm({
  coordinate1,
  coordinate2
}: {
  coordinate1: ICoordinates;
  coordinate2: ICoordinates;
}) {
  const { lat: lat1, lon: lon1 } = coordinate1;
  const { lat: lat2, lon: lon2 } = coordinate2;

  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);  // deg2rad below
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
    ;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d;
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180)
}

export const isValidPostalCodeFormat = (input: string): boolean => {
  const value = input.trim();

  // India (6 digits)
  const india = /^[1-9][0-9]{5}$/;

  // US ZIP (5 or 9)
  const us = /^\d{5}(-\d{4})?$/;

  // UK postcode (simplified but safe)
  const uk =
    /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

  return india.test(value) || us.test(value) || uk.test(value);
};

export const reverseGeocode = (zipCode: string): Promise<ICoordinates | null> => {
  return new Promise((resolve) => {
    if (!zipCode || !zipCode.trim()) {
      resolve(null);
      return;
    }

    if (!isValidPostalCodeFormat(zipCode)) {
      console.warn("Invalid postal code format", zipCode);
      resolve(null);
      return;
    }

    // Make sure google.maps is loaded (via <script> tag or Google Maps JS SDK)
    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
      console.error("Google Maps JavaScript API is not loaded");
      resolve(null);
      return;
    }

    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address: zipCode }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const result = results[0];

        // Check if result has postal code
        const isPostalCode = result.address_components?.some((c) =>
          c.types.includes("postal_code")
        );

        if (!isPostalCode) {
          console.warn("Not a real postal code", zipCode);
          resolve(null);
          return;
        }

        const location = result.geometry.location;

        if (!location || typeof location.lat !== "function" || typeof location.lng !== "function") {
          console.warn("Invalid coordinates returned from geocode", zipCode);
          resolve(null);
          return;
        }

        resolve({
          lat: location.lat(),
          lon: location.lng(),
        });
      } else {
        console.warn("Reverse geocode failed", zipCode, status);
        resolve(null);
      }
    });
  });
};